/**
 * Mastery Decay Engine
 *
 * RESOLVES: CONFLICT-07 (Mastery Never Decays)
 *
 * Implements a time-based forgetting curve for mastery scores.
 * Skills that haven't been practiced within configurable time windows
 * experience gradual mastery decay.
 *
 * Based on a simplified Ebbinghaus forgetting curve:
 * - No decay within the first 7 days (grace period)
 * - Gradual decay from day 7 to day 90
 * - Maximum decay caps at 30% of current mastery
 * - Higher-confidence mastery decays slower
 * - Decay is computed on-read, not stored (pure function)
 */

import { FamilyMasteryState } from '../types'

// ─── Configuration ────────────────────────────────────────────────────────────

export const DECAY_CONFIG = {
  /** Days before any decay begins (grace period) */
  GRACE_PERIOD_DAYS: 7,

  /** Days at which decay reaches maximum */
  MAX_DECAY_DAYS: 90,

  /** Maximum percentage of mastery that can decay (0.30 = 30%) */
  MAX_DECAY_RATIO: 0.3,

  /** Minimum mastery score after decay (never decay below this) */
  MIN_SCORE_AFTER_DECAY: 10,

  /** Confidence multiplier: high confidence (1.0) reduces decay by this factor */
  CONFIDENCE_PROTECTION_FACTOR: 0.5,

  /** Minimum attempts before decay applies (don't decay brand-new skills) */
  MIN_ATTEMPTS_FOR_DECAY: 3,

  /** Whether decay is enabled globally */
  ENABLED: true,
} as const

// ─── Core Decay Function ──────────────────────────────────────────────────────

/**
 * Compute the decayed mastery score for a family based on time since last practice.
 *
 * This is a PURE function — it does not modify the state.
 * Call this when reading mastery to get the effective score.
 *
 * @param state - The stored mastery state
 * @param now - Current timestamp (default: Date.now())
 * @returns The effective mastery score after decay
 */
export function computeDecayedMastery(
  state: FamilyMasteryState | undefined,
  now: number = Date.now(),
): number {
  if (!state || !DECAY_CONFIG.ENABLED) {
    return state?.masteryScore ?? 0
  }

  // Don't decay if too few attempts (unreliable data)
  if (state.attempts < DECAY_CONFIG.MIN_ATTEMPTS_FOR_DECAY) {
    return state.masteryScore
  }

  // Don't decay if never seen (lastSeen = 0)
  if (state.lastSeen === 0) {
    return state.masteryScore
  }

  const daysSinceLastSeen = (now - state.lastSeen) / (1000 * 60 * 60 * 24)

  // Grace period: no decay
  if (daysSinceLastSeen <= DECAY_CONFIG.GRACE_PERIOD_DAYS) {
    return state.masteryScore
  }

  // Compute decay factor (0 to MAX_DECAY_RATIO)
  const decayDays = daysSinceLastSeen - DECAY_CONFIG.GRACE_PERIOD_DAYS
  const maxDecayDays =
    DECAY_CONFIG.MAX_DECAY_DAYS - DECAY_CONFIG.GRACE_PERIOD_DAYS
  const rawDecayRatio = Math.min(1, decayDays / maxDecayDays)

  // Apply confidence protection: high confidence reduces decay
  const confidenceProtection =
    state.confidence * DECAY_CONFIG.CONFIDENCE_PROTECTION_FACTOR
  const effectiveDecayRatio = rawDecayRatio * (1 - confidenceProtection)

  // Calculate actual decay amount
  const decayAmount =
    state.masteryScore * DECAY_CONFIG.MAX_DECAY_RATIO * effectiveDecayRatio

  // Apply decay with floor
  const decayedScore = Math.max(
    DECAY_CONFIG.MIN_SCORE_AFTER_DECAY,
    Math.round(state.masteryScore - decayAmount),
  )

  return decayedScore
}

/**
 * Check if a mastery state has any active decay.
 * Useful for UI indicators ("not practiced recently").
 */
export function hasActiveDecay(
  state: FamilyMasteryState | undefined,
  now: number = Date.now(),
): boolean {
  if (!state || !DECAY_CONFIG.ENABLED) return false
  if (state.attempts < DECAY_CONFIG.MIN_ATTEMPTS_FOR_DECAY) return false
  if (state.lastSeen === 0) return false

  const daysSinceLastSeen = (now - state.lastSeen) / (1000 * 60 * 60 * 24)
  return daysSinceLastSeen > DECAY_CONFIG.GRACE_PERIOD_DAYS
}

/**
 * Get a human-readable decay status for UI display.
 */
export function getDecayStatus(
  state: FamilyMasteryState | undefined,
  now: number = Date.now(),
): 'fresh' | 'recent' | 'fading' | 'stale' {
  if (!state || state.lastSeen === 0 || state.attempts === 0) return 'fresh'

  const daysSinceLastSeen = (now - state.lastSeen) / (1000 * 60 * 60 * 24)

  if (daysSinceLastSeen <= DECAY_CONFIG.GRACE_PERIOD_DAYS) return 'recent'
  if (daysSinceLastSeen <= 30) return 'fading'
  return 'stale'
}

/**
 * Get the decay percentage for display (0-100).
 * Returns 0 if no decay is active.
 */
export function getDecayPercentage(
  state: FamilyMasteryState | undefined,
  now: number = Date.now(),
): number {
  if (!state) return 0

  const original = state.masteryScore
  const decayed = computeDecayedMastery(state, now)
  if (original <= 0) return 0

  return Math.round(((original - decayed) / original) * 100)
}

/**
 * Batch compute decayed mastery for all families.
 * Returns a new record with decayed scores (does not modify input).
 *
 * @param familyMastery - All family mastery states
 * @param now - Current timestamp
 * @returns Record with same keys but decayed masteryScore values
 */
export function computeAllDecayedMastery(
  familyMastery: Record<string, FamilyMasteryState>,
  now: number = Date.now(),
): Record<string, FamilyMasteryState> {
  const result: Record<string, FamilyMasteryState> = {}

  for (const [key, state] of Object.entries(familyMastery)) {
    const decayedScore = computeDecayedMastery(state, now)
    if (decayedScore !== state.masteryScore) {
      result[key] = {
        ...state,
        masteryScore: decayedScore,
      }
    } else {
      result[key] = state
    }
  }

  return result
}
